'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { createPortal } from 'react-dom';
import { Search, Menu, X, ChevronDown } from 'lucide-react';
import { getFormattedDates } from '@/lib/dateUtils';
import { PRIMARY_NAV_CATEGORIES, SECONDARY_NAV_CATEGORIES, normalizeCategorySlug } from '@/lib/categoryLabels';
import { useActiveNavCategory } from '@/lib/navActiveCategory';

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileOthersOpen, setMobileOthersOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Date Ticker State
    // Date Ticker State
    const [dateStrings, setDateStrings] = useState<{ staticDay: string; sliderItems: string[] }>({ staticDay: '', sliderItems: [] });
    const [currentDateIndex, setCurrentDateIndex] = useState(0);
    const [mounted, setMounted] = useState(false);
    const [isOnion, setIsOnion] = useState(false);

    const categories = PRIMARY_NAV_CATEGORIES;
    const pathname = usePathname();

    const isHomeActive = pathname === '/';

    // Set by NavCategoryReporter on /news/{id}/{slug} so the parent section
    // stays lit while reading an article. Already normalised by the reporter.
    const articleCategory = useActiveNavCategory();

    const isCategoryActive = (slug: string) => {
        const href = `/category/${slug}`;
        const onCategoryPage = pathname === href || pathname.startsWith(`${href}/`);
        const isArticleCategory = articleCategory !== null
            && articleCategory === normalizeCategorySlug(slug);
        return onCategoryPage || isArticleCategory;
    };

    const isOthersActive = SECONDARY_NAV_CATEGORIES.some((cat) => isCategoryActive(cat.slug));

    useEffect(() => {
        setMounted(true);
        if (typeof window !== 'undefined') {
            setIsOnion(window.location.hostname.endsWith('.onion'));
        }

        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);

        // Initialize Dates
        try {
            setDateStrings(getFormattedDates());
        } catch (e) {
            console.error("Failed to format dates:", e);
        }

        // Cycle Dates
        const interval = setInterval(() => {
            setCurrentDateIndex((prev) => (prev + 1) % 3);
        }, 2000);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearInterval(interval);
        };
    }, []);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
        }
    };

    return (
        <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? 'bg-white py-2 shadow-sm' : 'bg-white py-4'}`}>
            {/* Top Bar - Extra Sleek */}
            {!scrolled && (
                <div className="border-b border-slate-100 mb-4 pb-2 bg-slate-50/50">
                    <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-[11px] font-medium tracking-wider text-slate-500 uppercase">
                        <div className="flex items-center gap-3">
                            {/* Static Day Name */}
                            {mounted && dateStrings.staticDay && (
                                <div className="flex items-center gap-2">
                                    <span className="font-bengali text-xs text-slate-700 font-bold whitespace-nowrap">
                                        {dateStrings.staticDay}
                                    </span>
                                    <div className="h-4 w-px bg-slate-300 mx-1" />
                                </div>
                            )}

                            {/* Sliding Date Ticker */}
                            <div className="h-5 overflow-hidden relative w-40 xs:w-48 md:w-80">
                                {mounted && dateStrings.sliderItems && dateStrings.sliderItems.length > 0 && (
                                    <div
                                        key={currentDateIndex}
                                        className="font-bengali text-xs tracking-normal normal-case text-slate-600 font-medium absolute left-0 w-full truncate animate-fade-in-up"
                                        style={{ animationDuration: '400ms' }}
                                    >
                                        {dateStrings.sliderItems[currentDateIndex]}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center flex-shrink-0 ml-2">
                            <a
                                     href={isOnion ? "#" : "http://bangladesffeykpawrm3uzn3rkkvs52blto37yoaspusayzkhjj5eeqd.onion"}
                                target={isOnion ? "_self" : "_blank"}
                                rel="noreferrer"
                                className={`flex items-center gap-2 transition-colors font-bengali font-bold whitespace-nowrap ${isOnion ? 'text-green-600 hover:text-green-700' : 'hover:text-red-700 text-slate-600'}`}
                                title={isOnion ? "Tor সংযোগ সক্রিয়" : "Tor Onion Service"}
                            >
                                {/* Official Tor Browser onion mark — not the site favicon */}
                                <img
                                    src="/icons/tor-onion.svg"
                                    alt="Tor"
                                    width={16}
                                    height={16}
                                    className={`${isOnion ? 'opacity-100' : 'opacity-80'}`}
                                />
                                {/* Mobile Text: Hidden by default (md:hidden), but conditional inline if isOnion */}
                                <span className={`md:hidden ${isOnion ? 'inline' : 'hidden'}`}>
                                    {isOnion ? "Tor সক্রিয়" : ""}
                                </span>
                                {/* Desktop Text: Always visible on desktop (hidden md:inline) */}
                                <span className="hidden md:inline">
                                    {isOnion ? "Tor সংযোগ সক্রিয়" : "Tor সংস্করণ ভিসিট করুন"}
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Header Container */}
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex items-center justify-between gap-12">
                    {/* Logo & Brand */}
                    <div
                        className="flex-shrink-0 animate-fade-in-up"
                        style={{ animationDuration: '500ms' }}
                    >
                        <Link href="/" className="flex items-baseline gap-2 group">
                            <div className="text-3xl md:text-4xl font-extrabold text-slate-900 font-logo tracking-tight group-hover:text-red-700 transition-colors">
                                মুক্তকণ্ঠ<span className="text-red-600">.</span>
                            </div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden sm:block transform group-hover:translate-x-1 transition-transform font-bengali">
                                নিরপেক্ষ
                            </span>
                        </Link>
                    </div>

                    {/* Desktop Navigation - Enterprise Grade */}
                    <nav className="hidden lg:flex items-center flex-grow justify-center">
                        <ul className="flex items-center">
                            <li>
                                <Link
                                    href="/"
                                    aria-current={isHomeActive ? 'page' : undefined}
                                    className={`px-4 py-2 text-[15px] font-bold font-bengali transition-colors ${
                                        isHomeActive
                                            ? 'text-slate-900 border-b-2 border-red-600'
                                            : 'text-slate-600 hover:text-red-600 border-b-2 border-transparent'
                                    }`}
                                >
                                    প্রচ্ছদ
                                </Link>
                            </li>
                            {categories.map((cat) => {
                                const active = isCategoryActive(cat.slug);
                                return (
                                    <li key={cat.slug}>
                                        <Link
                                            href={`/category/${cat.slug}`}
                                            aria-current={active ? 'page' : undefined}
                                            className={`px-4 py-2 text-[15px] font-bold font-bengali transition-colors relative group ${
                                                active ? 'text-slate-900' : 'text-slate-600 hover:text-red-600'
                                            }`}
                                        >
                                            {cat.name}
                                            <span
                                                className={`absolute bottom-0 left-4 right-4 h-0.5 bg-red-600 transition-transform origin-left ${
                                                    active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                                                }`}
                                            />
                                        </Link>
                                    </li>
                                );
                            })}
                            <li className="relative group">
                                <button
                                    type="button"
                                    className={`px-4 py-2 text-[15px] font-bold font-bengali transition-colors flex items-center gap-1 ${
                                        isOthersActive ? 'text-slate-900' : 'text-slate-600 hover:text-red-600'
                                    }`}
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    অন্যান্য
                                    <ChevronDown className="w-4 h-4 opacity-60 transition-transform group-hover:rotate-180" />
                                </button>
                                <div className="absolute left-0 top-full hidden group-hover:block pt-1 z-50">
                                    <ul className="grid grid-cols-2 gap-2 w-max p-4 bg-white shadow-lg rounded-lg border border-slate-100">
                                        {SECONDARY_NAV_CATEGORIES.map((cat) => {
                                            const active = isCategoryActive(cat.slug);
                                            return (
                                                <li key={cat.slug}>
                                                    <Link
                                                        href={`/category/${cat.slug}`}
                                                        aria-current={active ? 'page' : undefined}
                                                        className={`block px-3 py-2 text-sm font-bold rounded-md font-bengali whitespace-nowrap transition-colors ${
                                                            active ? 'text-red-600 bg-red-50' : 'text-slate-700 hover:text-red-600 hover:bg-slate-50'
                                                        }`}
                                                    >
                                                        {cat.bn}
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </li>
                        </ul>
                    </nav>

                    {/* Quick Controls */}
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setSearchOpen(!searchOpen)}
                            className="p-2.5 text-slate-700 hover:bg-red-50 hover:text-red-600 rounded-full transition-all"
                            aria-label="অনুসন্ধান"
                        >
                            <Search className="w-5 h-5" />
                        </button>

                        <div className="h-6 w-px bg-slate-200 hidden md:block" />

                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden p-2.5 text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
                            aria-label={mobileMenuOpen ? 'মেনু বন্ধ করুন' : 'মেনু খুলুন'}
                            aria-expanded={mobileMenuOpen}
                            aria-controls="mobile-nav-drawer"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
                        </button>
                    </div>
                </div>

                {/* Sub-Header Features (Only when not scrolled) */}
                {searchOpen && (
                    <form
                        onSubmit={handleSearch}
                        className="mt-6 border-t border-slate-100 pt-6 animate-fade-in-up"
                        style={{ animationDuration: '300ms' }}
                    >
                        <div className="relative max-w-2xl mx-auto">
                            <input
                                type="text"
                                placeholder="মুক্তকণ্ঠ আর্কাইভে খুঁজুন..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                                className="w-full bg-slate-50 px-6 py-4 pr-16 text-lg border-0 rounded-2xl focus:ring-2 focus:ring-red-500 font-bengali shadow-inner"
                            />
                            <button
                                type="submit"
                                aria-label="অনুসন্ধান করুন"
                                className="absolute right-4 top-1/2 -translate-y-1/2 bg-red-600 text-white p-2 rounded-xl hover:bg-red-700 shadow-lg transition-all"
                            >
                                <Search className="w-6 h-6" aria-hidden="true" />
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Mobile Navigation Sidebar */}
            {mobileMenuOpen && typeof document !== 'undefined' && createPortal(
                <>
                    <div
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] animate-fade-in-up"
                        style={{ animationDuration: '300ms' }}
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    <div
                        id="mobile-nav-drawer"
                        role="dialog"
                        aria-modal="true"
                        aria-label="মোবাইল নেভিগেশন"
                        className="fixed top-0 right-0 flex h-full max-h-[100dvh] w-[85%] max-w-[400px] flex-col bg-white shadow-2xl z-[1001] animate-fade-in-up"
                        style={{ animationDuration: '400ms' }}
                    >
                        <div className="flex shrink-0 items-center justify-between px-6 pt-6 pb-4">
                            <div className="text-3xl font-extrabold text-slate-900 font-logo tracking-tight">
                                মুক্তকণ্ঠ<span className="text-red-600">.</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setMobileMenuOpen(false)}
                                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                                aria-label="মেনু বন্ধ করুন"
                            >
                                <X className="w-7 h-7 text-slate-500" aria-hidden="true" />
                            </button>
                        </div>

                        <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-4">
                            <ul className="space-y-1">
                                <li>
                                    <Link
                                        href="/"
                                        className={`flex items-center justify-between px-4 py-4 rounded-xl text-lg font-bold font-bengali transition-all ${
                                            isHomeActive ? 'text-red-600 bg-red-50' : 'text-slate-700 hover:text-red-600 hover:bg-slate-50'
                                        }`}
                                        aria-current={isHomeActive ? 'page' : undefined}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        প্রচ্ছদ
                                        <ChevronDown className="w-5 h-5 -rotate-90" />
                                    </Link>
                                </li>
                                {categories.map((cat) => {
                                    const active = isCategoryActive(cat.slug);
                                    return (
                                        <li key={cat.slug} className="border-b border-slate-50 last:border-0">
                                            <Link
                                                href={`/category/${cat.slug}`}
                                                aria-current={active ? 'page' : undefined}
                                                className={`flex items-center justify-between px-4 py-4 rounded-xl text-lg font-bold font-bengali transition-all ${
                                                    active ? 'text-red-600 bg-red-50' : 'text-slate-700 hover:text-red-600 hover:bg-slate-50'
                                                }`}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                {cat.name}
                                                <ChevronDown className="w-5 h-5 -rotate-90 opacity-40" />
                                            </Link>
                                        </li>
                                    );
                                })}
                                <li className="border-b border-slate-50">
                                    <button
                                        type="button"
                                        onClick={() => setMobileOthersOpen(!mobileOthersOpen)}
                                        className={`flex w-full items-center justify-between px-4 py-4 rounded-xl text-lg font-bold font-bengali transition-all ${
                                            isOthersActive ? 'text-red-600 bg-red-50' : 'text-slate-700 hover:text-red-600 hover:bg-slate-50'
                                        }`}
                                    >
                                        অন্যান্য
                                        <ChevronDown className={`w-5 h-5 opacity-40 transition-transform ${mobileOthersOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {mobileOthersOpen && (
                                        <ul className="grid grid-cols-2 gap-2 px-2 pb-4 pt-1">
                                            {SECONDARY_NAV_CATEGORIES.map((cat) => {
                                                const active = isCategoryActive(cat.slug);
                                                return (
                                                    <li key={cat.slug}>
                                                        <Link
                                                            href={`/category/${cat.slug}`}
                                                            aria-current={active ? 'page' : undefined}
                                                            className={`block px-3 py-2.5 text-sm font-bold rounded-lg font-bengali transition-colors ${
                                                                active ? 'text-red-600 bg-red-50' : 'text-slate-600 hover:text-red-600 hover:bg-slate-50'
                                                            }`}
                                                            onClick={() => {
                                                                setMobileMenuOpen(false);
                                                                setMobileOthersOpen(false);
                                                            }}
                                                        >
                                                            {cat.bn}
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </li>
                            </ul>
                        </nav>

                        {/* Mobile Tor Link */}
                        <div className="shrink-0 border-t border-slate-100 px-6 py-4">
                                <a
                                    href={isOnion ? "#" : "http://bangladesffeykpawrm3uzn3rkkvs52blto37yoaspusayzkhjj5eeqd.onion"}
                                    target={isOnion ? "_self" : "_blank"}
                                    rel="noreferrer"
                                    className={`flex items-center justify-between px-4 py-4 rounded-xl text-lg font-bold font-bengali transition-all ${isOnion ? 'text-green-600 bg-green-50' : 'text-slate-600 bg-slate-50 hover:bg-red-50 hover:text-red-600'}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <img
                                            src="/icons/tor-onion.svg"
                                            alt="Tor"
                                            width={20}
                                            height={20}
                                        />
                                        <span>{isOnion ? "Tor সংযোগ সক্রিয়" : "Tor সংস্করণ"}</span>
                                    </div>
                                    {!isOnion && <div className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full uppercase tracking-tighter">Onion</div>}
                            </a>
                        </div>
                    </div>
                </>,
                document.body
            )}
        </header>
    );
}
